/**
 * Welcher Knopf gehört zu welchem nächsten Schritt? Ein Hauptknopf, höchstens ein
 * Nebenknopf — beide führen dorthin, wo es die Handlung schon gibt (Editor,
 * Planer, Konto, Belegdetail mit Versand und Folgebeleg). Etappe 4 ersetzt
 * einzelne Ziele durch eigene Abläufe (Nachfassen, Erinnerung), die Knöpfe bleiben.
 */
import type { InvoiceRegisterItem, WebInvoiceDraftRecord } from "../../lib/invoices";
import type { ProjektLage } from "../../lib/projektLage";

export type Knopf =
  | { art: "link"; text: string; to: string }
  | { art: "sprung"; text: string; anker: string }
  | { art: "mail"; text: string; mailId: string }
  | { art: "beleg"; text: string; beleg: InvoiceRegisterItem; hinweis?: string }
  | { art: "angenommen"; text: string; beleg: InvoiceRegisterItem }
  | { art: "folgebeleg"; text: string; beleg: InvoiceRegisterItem; modus: "rechnung" | "schluss" }
  | { art: "abschliessen"; text: string }
  | { art: "keine_nacharbeit"; text: string }
  | { art: "bewertung"; text: string };

export type KopfKontext = {
  projektId: string;
  kundeId: string;
  status: string;
  /** Angebote und Rechnungen dieses Projekts aus dem Register. */
  belege: InvoiceRegisterItem[];
  /** Offene Web-Entwürfe (das Register liefert sie mit der ersten Seite). */
  entwuerfe?: WebInvoiceDraftRecord[];
};

const UNGUELTIG = ["entwurf", "abgelehnt", "storniert"];

/**
 * Aus welchem Angebot entsteht die Rechnung? (14.10.2026, Benjamin: „Ich möchte
 * nicht leer anfangen … es muss das aktuelle Angebot als Grundlage für die
 * Rechnung verwendet werden, mit dem Bezug zu dem Angebot.")
 *
 * Das aktuelle Angebot ist das jüngste angenommene — gibt es keins (oft wird die
 * Annahme nicht eingetragen, die Arbeit läuft trotzdem), das jüngste gestellte.
 * Eine Angebotsbestätigung zählt als neuere Fassung; der Server setzt den Bezug
 * dann auf das ursprüngliche Angebot. Liegen Abschläge vor, wird es eine
 * Schlussrechnung, die sie absetzt. Ist aus dem Angebot schon eine Rechnung
 * entstanden, gibt es keine Grundlage — sonst stünde der Auftrag zweimal in Rechnung.
 */
export function rechnungsGrundlage(belege: InvoiceRegisterItem[]): { angebot: InvoiceRegisterItem; modus: "rechnung" | "schluss" } | null {
  const angebote = belege
    .filter((b) => b.kind === "quote" && !UNGUELTIG.includes(b.status))
    .sort((a, b) => (a.issue_date || "").localeCompare(b.issue_date || ""));
  const angebot = [...angebote].reverse().find((b) => b.status === "angenommen") ?? angebote[angebote.length - 1];
  if (!angebot) return null;
  const nummern = new Set([angebot.nummer, angebot.source_quote_nummer].filter(Boolean));
  const gestellt = belege.filter((b) => b.kind !== "quote" && !UNGUELTIG.includes(b.status));
  if (gestellt.some((b) => (b.kind === "invoice" || b.kind === "final_invoice") && nummern.has(b.source_quote_nummer))) return null;
  return { angebot, modus: gestellt.some((b) => b.kind === "partial_invoice") ? "schluss" : "rechnung" };
}

/** Ein schon angefangener Rechnungsentwurf zu diesem Angebot — weiterschreiben statt einen zweiten anlegen. */
function offenerEntwurf(entwuerfe: WebInvoiceDraftRecord[], projektId: string, angebot: InvoiceRegisterItem) {
  const nummern = new Set([angebot.nummer, angebot.source_quote_nummer].filter(Boolean));
  return entwuerfe.find((d) => d.projekt_id === projektId && d.kind !== "quote" && d.kind !== "partial_invoice"
    && nummern.has(d.document?.source_quote_number || ""));
}

export function kopfKnoepfe(lage: ProjektLage, k: KopfKontext): { primaer: Knopf | null; zweit: Knopf | null } {
  const q = (kind: string) => `/rechnungen/neu?kind=${kind}&projekt=${k.projektId}${k.kundeId ? `&kunde=${k.kundeId}` : ""}`;
  const planen = `/planung?planen=1&projekt=${k.projektId}${k.kundeId ? `&kunde=${k.kundeId}` : ""}`;
  const konto = "/finanzen?reiter=konto";
  const s = lage.schritt;
  const beleg = (nummer?: string) => (nummer ? k.belege.find((b) => b.nummer === nummer) : undefined);
  const angebote = k.belege.filter((b) => b.kind === "quote");
  const angenommen = [...angebote].reverse().find((b) => b.status === "angenommen");
  const abgeschlossen = k.status.includes("abgeschlossen");

  if (lage.phase === "verloren" || lage.phase === "pausiert") return { primaer: null, zweit: null };

  if (lage.phase === "fertig") {
    if (!abgeschlossen) return { primaer: { art: "abschliessen", text: "Projekt abschließen" }, zweit: null };
    return { primaer: s?.aktion === "bewertung" ? { art: "bewertung", text: "Bewertung erbitten" } : null, zweit: null };
  }
  if (!s) return { primaer: null, zweit: null };

  const b = beleg(s.nummer);
  switch (s.aktion) {
    case "angebot_erstellen":
      return { primaer: { art: "link", text: s.text, to: q("quote") }, zweit: { art: "link", text: "Besichtigung planen", to: planen } };
    case "nachhaken":
      return {
        primaer: b ? { art: "beleg", text: s.text, beleg: b, hinweis: "Im Versand die Vorlage „Nachfassen“ wählen." } : null,
        zweit: b ? { art: "angenommen", text: "Kunde hat angenommen", beleg: b } : null,
      };
    case "angebot_angenommen":
      /* Etappe 8: klingt eine Kundenmail nach Zusage, steht die Mail als zweiter Knopf. */
      return {
        primaer: b ? { art: "angenommen", text: s.text, beleg: b } : null,
        zweit: s.mail_id ? { art: "mail", text: "Mail ansehen", mailId: s.mail_id }
          : b ? { art: "beleg", text: "Angebot ansehen", beleg: b } : null,
      };
    case "termine_planen":
      /* Bauprojekte planen im Arbeitsplan am Projekt (Etappe 6) — mit Plan nennt der
         Schritt, was fehlt; ohne Plan beginnt er dort. Der Planer bleibt der zweite Weg. */
      if (lage.projektart !== "pflege") {
        return {
          primaer: { art: "sprung", text: s.ort === "arbeitsplan" ? s.text : "Arbeitsplan anlegen", anker: "plan" },
          zweit: { art: "link", text: "Direkt im Planer planen", to: planen },
        };
      }
      return {
        primaer: { art: "link", text: s.text, to: planen },
        zweit: angenommen ? { art: "beleg", text: "Angebot ansehen", beleg: angenommen } : null,
      };
    case "nacharbeit_frage":
      /* Vor „Abrechnungsbereit" (20.10.2026, Gröger): fertig bestätigen oder die
         Nacharbeit planen — mit Arbeitsplan dort, sonst im Planer. */
      return {
        primaer: { art: "keine_nacharbeit", text: s.text },
        zweit: lage.plan && lage.plan.tage > 0
          ? { art: "sprung", text: "Nacharbeit planen", anker: "plan" }
          : { art: "link", text: "Nacharbeit planen", to: planen },
      };
    case "abhaken":
      /* Etappe 7: Tage vorbei, Leistungen nicht abgehakt — im Arbeitsplan abhaken
         oder als Nacharbeit planen. */
      return { primaer: { art: "sprung", text: s.text, anker: "plan" }, zweit: null };
    case "termin_oeffnen":
      return {
        primaer: { art: "link", text: s.text, to: s.datum ? `/planung?datum=${s.datum}` : "/planung" },
        zweit: { art: "link", text: "Einsatz planen", to: planen },
      };
    case "rechnung_schreiben": {
      /* Aus dem aktuellen Angebot, mit Bezug darauf — leer nur, wenn es keins gibt
         oder es schon abgerechnet ist (dann ist der Rest Nachtrag, kein Angebot). */
      const g = rechnungsGrundlage(k.belege);
      if (!g) return { primaer: { art: "link", text: s.text, to: q("invoice") }, zweit: null };
      const ansehen: Knopf = { art: "beleg", text: `Angebot ${g.angebot.nummer} ansehen`, beleg: g.angebot };
      const entwurf = offenerEntwurf(k.entwuerfe ?? [], k.projektId, g.angebot);
      if (entwurf) return { primaer: { art: "link", text: "Rechnungsentwurf weiterschreiben", to: `/rechnungen/entwurf/${entwurf.id}` }, zweit: ansehen };
      return {
        primaer: { art: "folgebeleg", text: g.modus === "schluss" ? "Schlussrechnung schreiben" : s.text, beleg: g.angebot, modus: g.modus },
        zweit: ansehen,
      };
    }
    case "zahlung_zuordnen":
      return {
        primaer: { art: "link", text: s.text, to: konto },
        zweit: b ? { art: "beleg", text: "Rechnung ansehen", beleg: b } : null,
      };
    case "zahlungserinnerung":
      return {
        primaer: b ? { art: "beleg", text: s.text, beleg: b, hinweis: "Im Versand die Vorlage „Zahlungserinnerung“ wählen." } : { art: "link", text: "Zahlung zuordnen", to: konto },
        zweit: b ? { art: "link", text: "Zahlung zuordnen", to: konto } : null,
      };
    default:
      return { primaer: null, zweit: null };
  }
}
